import { Plugin } from '@uppy/core';
import Translator from '@uppy/utils/lib/Translator';
import _ from 'lodash';
import { CancelToken } from 'axios';
import { timer } from '@utilities/helpers'; // eslint-disable-line
import VideoTranscoderService from '../services/VideoTranscoderService';

class UppyVimeoPlugin extends Plugin {
  constructor(uppy, opts = {}) {
    super(uppy, opts);
    this.id = opts.id || 'uppy-vimeo-plugin';
    this.teacherId = opts.teacherId;
    this.studentId = opts.studentId;
    this.pollingInterval = opts.pollingInterval || 30000; // 30 seconds
    this.pollingTimeout = opts.pollingTimeout || 3600000; // 1 hour
    this.vimeoAppId = null;
    this.vimeoAppIdsThatHaveAlreadyBeenTried = [];
    this.allVimeoAppIds = [];
    this.type = 'modifier';

    this.defaultLocale = {
      strings: {
        initializingPlaypositVideo: 'Initializing PlayPosit video...',
        updatingPlaypositVideo: 'Updating PlayPosit video...',
        queuedForTranscoding: 'Queued for transcoding',
        transcoding: 'Transcoding...',
        finalizingVideo: 'Finalizing video...',
      },
    };

    this.i18nInit();
    this.initializeVimeoAppId = this.initializeVimeoAppId.bind(this);
    this.cycleToNextVimeoAppId = this.cycleToNextVimeoAppId.bind(this);
    this.prepareUpload = this.prepareUpload.bind(this);
    this.handleUploadCompletion = this.handleUploadCompletion.bind(this);
    this.handleCancellation = this.handleCancellation.bind(this);
    this.poll = this.poll.bind(this);
  }

  setOptions(newOpts) {
    super.setOptions(newOpts);
    this.i18nInit();
  }

  i18nInit() {
    this.translator = new Translator([
      this.defaultLocale,
      this.uppy.locale,
      this.opts.locale,
    ]);
    this.i18n = this.translator.translate.bind(this.translator);
    this.setPluginState();
  }

  generateCancelToken() {
    this.cancelTokenSource = CancelToken.source();
  }

  cancelRequests() {
    if (_.isFunction(this?.cancelTokenSource?.cancel)) this.cancelTokenSource.cancel();
  }

  async handleCancellation() {
    this.cancelRequests();
    const files = this.uppy.getFiles() || [];
    if (!files.length) return;
    await Promise.all(files.map((file) => VideoTranscoderService.updateHostingLog({
      id: file?.hostingLog?.id,
      vimeoStatus: 'canceled',
      bunnyStatus: 'canceled',
    })));
  }

  areThereAnyRemainingUnusedVimeoAppIds() {
    return (
      !this.allVimeoAppIds.length
      || this.vimeoAppIdsThatHaveAlreadyBeenTried.length !== this.allVimeoAppIds.length
    );
  }

  async initializeVimeoAppId(forceNewAppId) {
    try {
      if (
        this.vimeoAppId
        && !forceNewAppId
      ) return;
      this.vimeoAppIdsThatHaveAlreadyBeenTried = _.compact(_.uniq([
        ...this.vimeoAppIdsThatHaveAlreadyBeenTried,
        this.vimeoAppId,
      ]));
      const { data: vimeoCredentialData } = await VideoTranscoderService.getVimeoCredentials({
        excludedAppIds: this.vimeoAppIdsThatHaveAlreadyBeenTried,
      }, { cancelToken: this.cancelTokenSource.token });
      this.vimeoAppId = vimeoCredentialData.appId;
      this.allVimeoAppIds = vimeoCredentialData.allAppIds;
    } catch(e) {
      this.uppy.log('[Uppy PlayPosit Vimeo] Failed to initialize a video: ', 'warning');
      this.uppy.log(e, 'warning');
    }
  }

  async cycleToNextVimeoAppId(fileId) {
    const file = _.cloneDeep(this.uppy.getFile(fileId));
    await this.initializeVimeoAppId(true);
    this.uppy.cancelAll();
    this.uppy.addFile(file);
  }

  async prepareUpload(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('preprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('initializingPlaypositVideo'),
        });
        this.generateCancelToken();
        await this.initializeVimeoAppId();
        const { data: vimeoData } = await VideoTranscoderService.initializeVimeoTranscode({
          vimeoAppId: this.vimeoAppId,
          teacherId: this.teacherId,
          blob: {
            name: file.name.split('.')[0],
            size: file.size,
          },
        }, { cancelToken: this.cancelTokenSource.token });
        if (file.type.split('/')[0] !== 'video') return false;
        this.uppy.getPlugin('Tus').setOptions({
          uploadUrl: vimeoData.upload.upload_link,
        });
        const response = await VideoTranscoderService.initializeTranscode({
          name: file.name,
          teacherId: this.teacherId,
          studentId: this.studentId,
        }, { cancelToken: this.cancelTokenSource.token });
        const {
          video: playpositVideo,
          hostingLog,
        } = response.data;
        this.uppy.setFileState(fileId, {
          uploadUrl: vimeoData.upload.upload_link,
          videoId: vimeoData.uri,
          videoUrl: `https://www.vimeo.com${vimeoData.uri}`,
          duration: file.duration,
          tus: {
            ...file.tus,
            uploadUrl: vimeoData.upload.upload_link,
            endpoint: vimeoData.upload.upload_link,
          },
          hostingLog,
          playpositVideo,
        });
        return file;
      } catch(e) {
        if (
          _.get(e, 'httpStatusCode') === 403
          && this.areThereAnyRemainingUnusedVimeoAppIds()
        ) {
          return this.cycleToNextVimeoAppId(fileId);
        }
        this.uppy.log('[Uppy PlayPosit Vimeo] Failed to initialize a video: ', 'warning');
        this.uppy.log(e, 'warning');
        return false;
      }
    });
    const emitPreprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('preprocess-complete', file);
      }
    };
    return Promise.all(promises)
      .then(emitPreprocessCompleteForAll);
  }

  poll({
    pollingFunction,
    file,
  } = {}) {
    const endTime = Number(new Date()) + this.pollingTimeout;
    let hasDuration = false;
    let timeoutId;

    function clearPollingTimeout() {
      clearTimeout(timeoutId);
    }

    this.uppy.off('file-removed', clearPollingTimeout);
    this.uppy.off('cancel-all', clearPollingTimeout);
    this.uppy.on('file-removed', clearPollingTimeout);
    this.uppy.on('cancel-all', clearPollingTimeout);

    const checkCondition = async (resolve, reject) => {
      try {
        const response = await pollingFunction();
        if (
          !hasDuration
          && _.isFinite(_.get(response, 'data.duration'))
        ) {
          hasDuration = true;
          await VideoTranscoderService.updateVimeoTranscode({
            videoId: file.playpositVideo.id,
            duration: _.get(response, 'data.duration'),
            teacherId: this.teacherId,
          }, { cancelToken: this.cancelTokenSource.token });
        }
        if (_.get(response, 'data.progress')) {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: parseFloat(response.data.progress) / 100,
          });
        }
        if (
          _.get(response, 'data.vimeoTranscodeStatus') === 'ready'
          || [
            'finished',
            'resolutionFinished',
          ].includes(_.get(response, 'data.bunnyTranscodeStatus'))
        ) {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: 1,
          });
          resolve(response.data);
        } else if (Number(new Date()) < endTime) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          reject(new Error('Timed out.'));
        }
      } catch(e) {
        if (_.get(e, 'response.status') === 404) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          clearPollingTimeout();
          reject(e);
        }
      }
    };

    return new Promise(checkCondition);
  }

  async handleUploadCompletion(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('queuedForTranscoding'),
          value: 0,
        });
        const vimeoVideoId = parseInt(file.videoId.replace('/videos/', ''), 10);
        await VideoTranscoderService.createTeacherVideo({
          name: file.name,
          videoId: file.hostingLog.videoId,
          teacherId: this.teacherId,
        }, { cancelToken: this.cancelTokenSource.token });
        this.uppy.emit('teacher-video-created');
        const pollData = await this.poll({
          pollingFunction: () => VideoTranscoderService.getVimeoUploadStatus({
            vimeoVideoId,
            vimeoAppId: this.vimeoAppId,
            playpositVideoId: file.playpositVideo.id,
            teacherId: this.teacherId,
            studentId: this.studentId,
          }, { cancelToken: this.cancelTokenSource.token }),
          file,
        });
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('finalizingVideo'),
          value: 0,
        });
        await timer(10000);
        this.uppy.setFileState(fileId, {
          playpositVideo: _.get(pollData, 'updatedPlaypositVideo'),
          pollData: _.omit(pollData, 'updatedPlaypositVideo'),
          duration: _.get(pollData, 'updatedPlaypositVideo.playerDuration'),
        });
        return this.uppy.getFile(fileId);
      } catch(e) {
        if (
          _.get(e, 'httpStatusCode') === 403
          && this.areThereAnyRemainingUnusedVimeoAppIds()
        ) {
          return this.cycleToNextVimeoAppId(fileId);
        }
        this.uppy.log('[Uppy PlayPosit Vimeo] Failed to update a video:', 'warning');
        this.uppy.log(e, 'warning');
        this.uppy.cancelAll();
        this.uppy.emit('error', e);
        throw e;
      }
    });

    const emitPostprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        if (file) this.uppy.emit('postprocess-complete', file);
      }
    };

    return Promise.all(promises)
      .then(emitPostprocessCompleteForAll);
  }

  install() {
    this.uppy.addPreProcessor(this.prepareUpload);
    this.uppy.addPostProcessor(this.handleUploadCompletion);
    this.uppy.on('cancel-all', this.handleCancellation);
  }

  uninstall() {
    this.uppy.removePreProcessor(this.prepareUpload);
    this.uppy.removePostProcessor(this.handleUploadCompletion);
    this.uppy.off('cancel-all', this.handleCancellation);
  }
}

export default UppyVimeoPlugin;
